import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, Zap, AlertTriangle, Cpu, Code, Search, Brain, CheckCircle, ArrowRight, Globe, Server, Users, Award } from 'lucide-react';
import Header from "./header";
import Footer from "./footer";
const Cybersecurity = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    const observeElements = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(prev => ({
                ...prev,
                [entry.target.id]: true
              }));
            }
          });
        },
        { threshold: 0.1 }
      );

      elements.forEach((el) => observer.observe(el));
    };

    observeElements();
  }, []);
  const services1 = [
    {
      icon: <Cpu className="w-8 h-8 text-red-500" />,
      title: "IoT Security",
      description: "Comprehensive security solutions for Internet of Things devices, networks, and ecosystems to protect against emerging threats"
    },
    {
      icon: <Code className="w-8 h-8 text-red-500" />,
      title: "Open Source Code Security",
      description: "Advanced vulnerability assessment and security auditing for open source components and dependencies"
    },
    {
      icon: <Brain className="w-8 h-8 text-red-500" />,
      title: "Cognitive Security",
      description: "AI-powered threat detection and behavioral analysis to identify sophisticated cyber attacks and anomalies"
    },
    {
      icon: <Shield className="w-8 h-8 text-red-500" />,
      title: "Enterprise Security",
      description: "Complete cybersecurity framework implementation with risk assessment and compliance management"
    }
  ];

  const iotFeatures = [
    "Device authentication and authorization protocols",
    "Secure communication channels and encryption",
    "Firmware security and update management",
    "Network segmentation and monitoring",
    "Vulnerability assessment for IoT ecosystems",
    "Compliance with IoT security standards and regulations"
  ];

  const openSourceFeatures = [
    "Automated vulnerability scanning of dependencies",
    "License compliance and risk assessment",
    "Code quality and security best practices",
    "Continuous monitoring of security advisories",
    "Supply chain security validation",
    "Custom security policies for open source adoption"
  ];

  const cognitiveFeatures = [
    "Machine learning-based threat detection",
    "Behavioral analytics and anomaly detection",
    "Predictive security intelligence",
    "Advanced persistent threat (APT) identification",
    "Real-time risk scoring and assessment",
    "Automated incident response and remediation"
  ];

  const services = [
    {
      icon: Shield,
      title: "OT Security",
      description: "Robust OT security solutions that safeguard industrial control systems, SCADA networks, and critical infrastructure against evolving cyber threats.",
      features: [
        "ICS/SCADA protection",
        "Industrial network monitoring",
        "Critical infrastructure defense"
      ]
    }
    ,
    {
      icon: Lock,
      title: "Physical Security Systems",
      description: "Integrated physical security solutions combining access control, surveillance, and perimeter protection for complete facility security.",
      features: ["Access control systems", "Video surveillance", "Perimeter security"]
    },
    {
      icon: Eye,
      title: "Security Monitoring & Analytics",
      description: "24/7 security monitoring with advanced analytics to provide comprehensive visibility into your security posture.",
      features: ["24/7 SOC monitoring", "Advanced analytics", "Custom dashboards"]
    },
    {
      icon: Zap,
      title: "Incident Response & Recovery",
      description: "Rapid incident response and recovery services to minimize downtime and restore operations quickly.",
      features: ["Rapid response team", "Forensic analysis", "Recovery planning"]
    }
  ];

  const stats = [
    { icon: Shield, value: "99.9%", label: "Threat Detection Rate" },
    { icon: Users, value: "500+", label: "Protected Clients" },
    { icon: Globe, value: "24/7", label: "Global Monitoring" },
    { icon: Award, value: "ISO 27001", label: "Certified Security" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-40 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
      </div>

      <Header />
      <section className="relative px-6 py-20 text-center min-h-screen flex items-center">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="./Animatecybersecurity.mp4" type="video/mp4" />
            {/* Fallback for browsers that don't support video */}
            <img src="security.jpg" alt="Security background" className="w-full h-full object-cover" />
          </video>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10"
            id="hero-title"
            style={{ opacity: isVisible['hero-title'] ? 1 : 0, transform: isVisible['hero-title'] ? 'translateY(0)' : 'translateY(40px)' }}>

            <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
              Protect your digital assets with our comprehensive cybersecurity solutions.
              Advanced threat detection, real-time monitoring, and expert response teams.
            </p>
          </div>
        </div>
      </section>



      <section className="bg-white py-20 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Privacy and <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Security</span> Compliance
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Enterprise-grade security and compliance solutions designed to protect your business
              and ensure regulatory adherence across all platforms and services.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Protection</h3>
              <p className="text-gray-600">
                Advanced encryption and data protection measures to safeguard sensitive information.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Compliance Monitoring</h3>
              <p className="text-gray-600">
                Real-time monitoring and reporting for GDPR, CCPA, and other regulatory requirements.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Audits</h3>
              <p className="text-gray-600">
                Regular security assessments and vulnerability testing to maintain security standards.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Section 2: Light Services Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10 text-center mb-16"
            id="services-title"
            style={{ opacity: isVisible['services-title'] ? 1 : 0, transform: isVisible['services-title'] ? 'translateY(0)' : 'translateY(40px)' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive cybersecurity solutions designed to protect your business from evolving threats
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index}
                className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10"
                id={`service-${index}`}
                style={{
                  opacity: isVisible[`service-${index}`] ? 1 : 0,
                  transform: isVisible[`service-${index}`] ? 'translateY(0)' : 'translateY(40px)',
                  transitionDelay: `${index * 200}ms`
                }}>
                <div className="relative group">
                  {/* Animated background behind card */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl opacity-20 animate-pulse"></div>

                  {/* Card content */}
                  <div className="relative bg-white/95 backdrop-blur-md rounded-2xl p-8 border border-gray-200 hover:border-cyan-300 hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-500 transform hover:scale-105 group">
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-700">
                          <CheckCircle className="w-5 h-5 text-cyan-500 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Section 6: Light CTA Section */}
      <section className="bg-white text-gray-800 px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10"
            id="cta-section"
            style={{ opacity: isVisible['cta-section'] ? 1 : 0, transform: isVisible['cta-section'] ? 'translateY(0)' : 'translateY(40px)' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Ready to <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Secure</span> Your Future?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Don't wait for a security breach to happen. Protect your business today with our comprehensive cybersecurity solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-cyan-500 to-blue-500 px-8 py-4 rounded-full font-semibold text-lg text-white hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2">
                <a
                  href="/contact"
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 px-8 py-4 rounded-full font-semibold text-lg text-white hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  Get Protected Now <Shield className="w-5 h-5" />
                </a>              </button>
              <button className="border-2 border-cyan-500 px-8 py-4 rounded-full font-semibold text-lg text-cyan-600 hover:bg-cyan-500 hover:text-white transition-all duration-300 transform hover:scale-105">
                <a
                  href="/contact"
                  className="border-2 border-cyan-500 px-8 py-4 rounded-full font-semibold text-lg text-cyan-600 hover:bg-cyan-500 hover:text-white transition-all duration-300 transform hover:scale-105"
                >
                  Schedule Consultation
                </a>              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      {/* Footer */}

    </div>
  );
};

export default Cybersecurity;
